import * as Location from 'expo-location';
import { createContext, useContext, useEffect, useState } from 'react';

export const GeoContext = createContext();

export const GeoProvider = ({ children }) => {
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [heading, setHeading] = useState(null);
  const [hasGPSAccess, setHasGPSAccess] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [watchId, setWatchId] = useState(null);
  // Check permission status on initialization (without requesting) and
  // subscribe to permissions changes in browsers that support the Permissions API.
  useEffect(() => {
    let permissionWatcher = null;

    const checkInitialPermissionStatus = async () => {
      try {
        const { status } = await Location.getForegroundPermissionsAsync();
        setHasGPSAccess(status === 'granted');
        if (status !== 'granted') {
          setLocationError('Location permission not granted');
        } else {
          setLocationError(null);
        }
      } catch (error) {
        setLocationError(error.message);
        setHasGPSAccess(false);
      }
    };

    checkInitialPermissionStatus();

    // If the browser supports the Permissions API, listen for changes so
    // the UI updates when the user changes site permissions externally.
    if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
      try {
        navigator.permissions.query({ name: 'geolocation' }).then((perm) => {
          permissionWatcher = perm;
          perm.onchange = async () => {
            // When permission changes, re-check and (if granted) ensure we're watching
            try {
              const { status } = await Location.getForegroundPermissionsAsync();
              setHasGPSAccess(status === 'granted');
              if (status === 'granted') {
                setLocationError(null);
                // Start watching if not already running
                if (!watchId) {
                  startWatching().catch(() => {});
                }
              } else {
                setLocationError('Location permission not granted');
                // Stop watching if permission revoked
                stopWatching();
              }
            } catch (err) {
              console.error('Error handling permission change:', err);
            }
          };
        }).catch(() => {});
      } catch (_e) {
        // ignore permission API errors
      }
    }

    // Cleanup function
    return () => {
      if (permissionWatcher && permissionWatcher.onchange) {
        permissionWatcher.onchange = null;
      }
      // Ensure any active subscription is removed on unmount
      if (watchId && watchId.remove) {
        try { watchId.remove(); } catch (_e) {}
      }
    };
    // We intentionally run this effect once on mount so we don't repeatedly reattach
    // listeners when watchId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Get current position once (not watching)
  const getCurrentPosition = async () => {
    try {
      // Check permissions first
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        throw new Error('Location permission not granted');
      }

      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
        maximumAge: 60000,
      });

      const coords = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude
      };
      setCoordinates(coords);
      return coords;
    } catch (error) {
      setLocationError(error.message);
      throw error;
    }
  };

  // Stop watching location
  const stopWatching = () => {
    if (watchId && watchId.remove) {
      try {
        watchId.remove();
      } catch (e) {
        console.error('Error removing watch subscription:', e);
      }
      setWatchId(null);
    }
  };

  // Start watching location again
  const startWatching = async () => {
    if (!watchId) {
      // Use Expo Location for native, fallback to navigator.geolocation for web
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        // Web fallback
        const geoWatchId = navigator.geolocation.watchPosition(
          (pos) => {
            setCoordinates({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            });
            console.log('Web geolocation updated:', pos.coords); // Debug log
            setHasGPSAccess(true);
            setLocationError(null);
            // Heading not available in web API
          },
          (err) => {
            setLocationError(err.message);
            setHasGPSAccess(false);
          },
          {
            enableHighAccuracy: true,
            maximumAge: 10000,  // Allow cached positions up to 10 seconds old
            timeout: 15000      // Timeout after 15 seconds
          }
        );
        setWatchId({ remove: () => navigator.geolocation.clearWatch(geoWatchId) });
      } else {
        // Native/Expo fallback
        try {
          const { status } = await Location.requestForegroundPermissionsAsync();
          if (status !== 'granted') {
            setLocationError('Location permission not granted');
            setHasGPSAccess(false);
            return;
          }
          const subscription = await Location.watchPositionAsync(
            {
              accuracy: Location.Accuracy.High,
              timeInterval: 5000,
              distanceInterval: 5,
            },
            (location) => {
              setCoordinates({
                latitude: location.coords.latitude,
                longitude: location.coords.longitude
              });
              console.log('Location updated:', location.coords); // Debug log
              setHasGPSAccess(true);
              setLocationError(null);
              if (location.coords.heading !== null && location.coords.heading !== undefined) {
                setHeading(location.coords.heading);
              }
            }
          );
          setWatchId(subscription);
        } catch (error) {
          console.error('Location error:', error);
          setLocationError(error.message);
          setHasGPSAccess(false);
        }
      }
    }
  };

  // Request location permissions explicitly and start tracking
  const requestPermissions = async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status === 'granted') {
        setHasGPSAccess(true);
        setLocationError(null);
        // Automatically start watching location after permissions granted
        await startWatching();
        // Also request a single current position to populate coordinates quickly
        try {
          await getCurrentPosition();
        } catch (_) {}
        return true;
      } else {
        setHasGPSAccess(false);
        setLocationError('Location permission denied');
        return false;
      }
    } catch (error) {
      setLocationError(error.message);
      setHasGPSAccess(false);
      return false;
    }
  };

  // Check current permission status
  const checkPermissions = async () => {
    try {
      const { status } = await Location.getForegroundPermissionsAsync();
      setHasGPSAccess(status === 'granted');
      if (status !== 'granted') {
        setLocationError('Location permission not granted');
      } else {
        setLocationError(null);
      }
      return status === 'granted';
    } catch (error) {
      setLocationError(error.message);
      setHasGPSAccess(false);
      return false;
    }
  };

  // Initialize location tracking if permissions are already granted
  const initializeLocationTracking = async () => {
    try {
      const { status } = await Location.getForegroundPermissionsAsync();
      if (status === 'granted' && !watchId) {
        await startWatching();
      }
    } catch (error) {
      console.error('Failed to initialize location tracking:', error);
    }
  };


// Calculate distance between two coordinates (Haversine formula)
 const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  return distance * 1000; // Convert to meters
};

const friendlyDistance = (lat1, lon1, lat2, lon2) => {
  let rawdistance = calculateDistance(lat1, lon1, lat2, lon2);
  if (rawdistance > 1000000) {
    return "---";
  } else if (rawdistance > 5000) {
    return "" + Math.round(rawdistance / 1000) + "km";
  } else if (rawdistance < 10) {
    return "Close";
  } else {
    return Math.round(rawdistance) + "m";
  }
}

const distanceToLocation = (location) => {
  if (!location || location.latitude == null || location.longitude == null) {
    return null;
  }
  if (coordinates.latitude == null || coordinates.longitude == null) {
    return null;
  }
  return calculateDistance(
    coordinates.latitude,
    coordinates.longitude,
    location.latitude,
    location.longitude
  );
}

const calculateDirection = (lat1, lon1, lat2, lon2) => {
  const phiK = lat1 * Math.PI / 180.0;
  const lambdaK = lon1 * Math.PI / 180.0;
  const phi = lat2 * Math.PI / 180.0;
  const lambda = lon2 * Math.PI / 180.0;

  const psi = 180.0/Math.PI * Math.atan2(Math.sin(lambdaK - lambda) , Math.cos(phi) * Math.tan(phiK) - Math.sin(phi) * Math.cos(lambdaK - lambda));
  return Math.round(psi);
}

const isWithinLocationRange = (location) => {
  if (!location || !location.latitude || !location.longitude || !location.radius) {
    return false;
  }
  return isWithinRange(location.latitude, location.longitude, location.radius);
}

// Check if user is within range of a location
const isWithinRange = (targetLat, targetLon, rangeInMeters = 100) => {
  if (coordinates.latitude == null || coordinates.longitude == null) {
    return false;
  }
  
  const distance = calculateDistance(
    coordinates.latitude,
    coordinates.longitude,
    targetLat,
    targetLon
  );
  
  return distance <= rangeInMeters;
};

const friendlyDirection = (lat1, lon1, lat2, lon2) => {
  let degrees = calculateDirection(lat1, lon1, lat2, lon2);
  if (degrees < 0) {
    degrees = 360 + degrees;
  }
  if (degrees >= 337 || degrees < 22) {
    return "N";
  } else if (degrees >= 22 && degrees < 67) {
    return "NE";
  } else if (degrees >= 67 && degrees < 112) {
    return "E";
  } else if (degrees >= 112 && degrees < 157) {
    return "SE";
  } else if (degrees >= 157 && degrees < 202) {
    return "S";
  } else if (degrees >= 202 && degrees < 247) {
    return "SW";
  } else if (degrees >= 247 && degrees < 292) {
    return "W";
  } else if (degrees >= 292 && degrees < 337) {
    return "NW";
  }
}

  return (
    <GeoContext.Provider value={{
      coordinates,
      setCoordinates,
      heading,
      setHeading,
      hasGPSAccess,
      setHasGPSAccess,
      locationError,
      setLocationError,
      calculateDistance,
      distanceToLocation,
      isWithinRange,
      isWithinLocationRange,
      getCurrentPosition,
      stopWatching,
      startWatching,
      requestPermissions,
      checkPermissions,
      initializeLocationTracking,
      friendlyDistance,
      friendlyDirection,
      watchId
    }}>
      {children}
    </GeoContext.Provider>
  );
};

// Custom hook for using Geo context
export const useGeo = () => {
  const context = useContext(GeoContext);
  if (!context) {
    throw new Error('useGeo must be used within a GeoProvider');
  }
  return context;
};

export default GeoProvider;